import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { wsClient } from '../services/websocket-client.js';
import { router, navigateTo } from '../services/router.js';
import { AudioCaptureService } from '../services/audio-capture-service.js';
import { AudioPlaybackService } from '../services/audio-playback-service.js';
import '../components/voice/cp-audio-visualizer.js';
import '../components/voice/cp-call-controls.js';
import '../components/voice/cp-action-log.js';
import '../components/voice/cp-call-transcript.js';
import type { CpAudioVisualizer } from '../components/voice/cp-audio-visualizer.js';
import type { VoiceAction, ActionStatus } from '../components/voice/cp-action-log.js';
import type { TranscriptMessage } from '../components/voice/cp-call-transcript.js';

type CallState = 'connecting' | 'active' | 'ended' | 'error';
type InputMode = 'ptt' | 'vad';

interface AgentInfo {
  name: string;
  role: string;
  avatar: string;
}

@customElement('cp-page-voice-call')
export class CpPageVoiceCall extends LitElement {
  createRenderRoot() { return this; }

  @state() private _callState: CallState = 'connecting';
  @state() private _sessionId = '';
  @state() private _agentInfo: AgentInfo = { name: 'Agent', role: 'Assistant', avatar: '' };
  @state() private _isMuted = false;
  @state() private _callDuration = 0;
  @state() private _errorMessage = '';
  @state() private _inputMode: InputMode = 'vad';
  @state() private _isAgentSpeaking = false;
  @state() private _textMode = false;
  @state() private _micAvailable = true;
  @state() private _actions: VoiceAction[] = [];
  @state() private _transcriptMessages: TranscriptMessage[] = [];

  private _transcriptCounter = 0;
  private _durationTimer: ReturnType<typeof setInterval> | null = null;
  private _previousPath = '/team';
  private _audioCapture: AudioCaptureService | null = null;
  private _audioPlayback: AudioPlaybackService | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._onWsMessage);

    // Read skillId from route params
    const params = router.getParams();
    const skillId = params['skillId'] ?? '';

    // Extract agent info from URL params (passed as query-like hash params)
    const hash = window.location.hash;
    const nameMatch = hash.match(/[?&]name=([^&]*)/);
    const roleMatch = hash.match(/[?&]role=([^&]*)/);
    const avatarMatch = hash.match(/[?&]avatar=([^&]*)/);

    if (nameMatch) this._agentInfo = { ...this._agentInfo, name: decodeURIComponent(nameMatch[1]) };
    if (roleMatch) this._agentInfo = { ...this._agentInfo, role: decodeURIComponent(roleMatch[1]) };
    if (avatarMatch) this._agentInfo = { ...this._agentInfo, avatar: decodeURIComponent(avatarMatch[1]) };

    // Start the call
    this._startCall(skillId);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._onWsMessage);
    this._stopDurationTimer();
    this._stopAudio();

    // End the call if still active
    if (this._sessionId && this._callState === 'active') {
      wsClient.sendVoiceCallEnd(this._sessionId);
    }
  }

  private _startCall(skillId: string): void {
    this._callState = 'connecting';

    // Check if voice is configured
    if (wsClient.connectionState !== 'connected') {
      this._callState = 'error';
      this._errorMessage = 'WebSocket nicht verbunden';
      return;
    }

    // Send call start with skillId for transcript and persona lookup
    wsClient.sendVoiceCallStart(skillId || undefined);
  }

  private async _initAudio(): Promise<void> {
    // Initialize audio playback (agent TTS) - always needed
    this._audioPlayback = new AudioPlaybackService();
    this._audioPlayback.on('stateChange', (state) => {
      this._isAgentSpeaking = state === 'playing';
    });

    // Try to initialize audio capture (microphone)
    this._audioCapture = new AudioCaptureService();
    this._audioCapture.on('chunk', this._onAudioChunk);
    this._audioCapture.on('error', (err) => {
      console.error('Audio capture error:', err.message);
    });

    // Start microphone if in VAD mode - detect availability
    if (this._inputMode === 'vad') {
      try {
        await this._audioCapture.start();
        this._micAvailable = true;
      } catch {
        // Microphone not available - auto-fallback to text mode
        this._micAvailable = false;
        this._textMode = true;
        console.warn('Microphone not available, falling back to text mode');
      }
    }
  }

  private _stopAudio(): void {
    if (this._audioCapture) {
      this._audioCapture.stop();
      this._audioCapture = null;
    }
    if (this._audioPlayback) {
      this._audioPlayback.destroy();
      this._audioPlayback = null;
    }

    // Disconnect visualizers
    const agentViz = this.querySelector<CpAudioVisualizer>('cp-audio-visualizer[source="agent"]');
    const userViz = this.querySelector<CpAudioVisualizer>('cp-audio-visualizer[source="user"]');
    agentViz?.disconnect();
    userViz?.disconnect();
  }

  private _onAudioChunk = (data: ArrayBuffer): void => {
    if (!this._sessionId || this._isMuted) return;

    // Convert ArrayBuffer to base64
    const bytes = new Uint8Array(data);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);

    wsClient.sendVoiceAudioChunk(this._sessionId, base64);
  };

  private _onWsMessage = (e: Event): void => {
    const detail = (e as CustomEvent).detail as { type: string; payload: Record<string, unknown> };
    const { type, payload } = detail;

    switch (type) {
      case 'voice:call:started':
        this._sessionId = payload.sessionId as string;
        this._callState = 'active';
        this._startDurationTimer();
        this._initAudio();
        break;

      case 'voice:call:ended':
        if (payload.sessionId === this._sessionId) {
          this._callState = 'ended';
          this._stopDurationTimer();
          this._stopAudio();
          // Navigate back after short delay
          setTimeout(() => this._navigateBack(), 500);
        }
        break;

      case 'voice:tts:chunk':
        if (payload.sessionId === this._sessionId && this._audioPlayback) {
          this._audioPlayback.enqueue(payload.audio as string);
        }
        break;

      case 'voice:tts:end':
        // State will be updated by AudioPlaybackService stateChange event
        break;

      case 'voice:transcript:interim':
        if (payload.sessionId === this._sessionId) {
          this._handleTranscript(payload.text as string, 'user', true);
        }
        break;

      case 'voice:transcript:final':
        if (payload.sessionId === this._sessionId) {
          this._handleTranscript(payload.text as string, 'user', false);
        }
        break;

      case 'voice:tts:start':
        this._isAgentSpeaking = true;
        if (payload.text) {
          this._addTranscriptMessage(payload.text as string, 'agent');
        }
        break;

      case 'voice:action:start':
        if (payload.sessionId === this._sessionId) {
          this._addAction({
            actionId: payload.actionId as string,
            tool: payload.tool as string,
            description: payload.description as string,
            status: 'running',
            timestamp: new Date().toISOString(),
          });
        }
        break;

      case 'voice:action:complete':
        if (payload.sessionId === this._sessionId) {
          this._updateActionStatus(
            payload.actionId as string,
            (payload.success as boolean) ? 'complete' : 'error',
            payload.result as string | undefined,
          );
        }
        break;

      case 'voice:error':
        if (payload.sessionId === this._sessionId || !this._sessionId) {
          this._callState = 'error';
          this._errorMessage = (payload.error as string) ?? 'Unbekannter Fehler';
          this._stopDurationTimer();
          this._stopAudio();
        }
        break;
    }
  };

  private _startDurationTimer(): void {
    this._callDuration = 0;
    this._durationTimer = setInterval(() => {
      this._callDuration++;
    }, 1000);
  }

  private _stopDurationTimer(): void {
    if (this._durationTimer !== null) {
      clearInterval(this._durationTimer);
      this._durationTimer = null;
    }
  }

  private _formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  private _onMuteToggle(): void {
    this._isMuted = !this._isMuted;

    if (this._isMuted) {
      this._audioCapture?.stop();
    } else if (this._inputMode === 'vad') {
      this._audioCapture?.start();
    }
  }

  private _onHangUp(): void {
    if (this._sessionId) {
      wsClient.sendVoiceCallEnd(this._sessionId);
    }
    this._callState = 'ended';
    this._stopDurationTimer();
    this._stopAudio();
    setTimeout(() => this._navigateBack(), 300);
  }

  private async _onPttStart(): Promise<void> {
    if (this._isMuted) return;

    // Barge-in: stop agent TTS when user starts speaking
    if (this._audioPlayback?.isPlaying) {
      this._audioPlayback.stop();
      if (this._sessionId) {
        wsClient.sendVoiceTtsStop(this._sessionId);
      }
    }

    await this._audioCapture?.start();
  }

  private _onPttEnd(): void {
    this._audioCapture?.stop();
  }

  private _onModeChange(e: CustomEvent<{ mode: InputMode }>): void {
    const newMode = e.detail.mode;
    this._inputMode = newMode;

    if (newMode === 'vad' && !this._isMuted) {
      // Start continuous capture in VAD mode
      this._audioCapture?.start();
    } else if (newMode === 'ptt') {
      // Stop capture in PTT mode (user must hold button)
      this._audioCapture?.stop();
    }
  }

  private async _onTextModeToggle(): Promise<void> {
    if (this._textMode) {
      // Switching back to voice mode - try to acquire mic
      if (!this._micAvailable) {
        try {
          await this._audioCapture?.start();
          this._micAvailable = true;
        } catch {
          // Still no mic - stay in text mode
          console.warn('Microphone still not available');
          return;
        }
      }
      this._textMode = false;
      // Resume audio capture if in VAD mode
      if (this._inputMode === 'vad' && !this._isMuted) {
        this._audioCapture?.start();
      }
    } else {
      // Switching to text mode
      this._textMode = true;
      // Stop audio capture
      this._audioCapture?.stop();
    }
  }

  // Handles text-send event from cp-call-controls, sends voice:text:send via WS
  private _onTextSend(e: CustomEvent<{ text: string }>): void {
    const { text } = e.detail;
    if (!text || !this._sessionId) return;

    // Barge-in: stop agent TTS when user sends text
    if (this._audioPlayback?.isPlaying) {
      this._audioPlayback.stop();
      wsClient.sendVoiceTtsStop(this._sessionId);
    }

    wsClient.sendVoiceTextMessage(this._sessionId, text);
  }

  private _addAction(action: VoiceAction): void {
    this._actions = [...this._actions, action];
  }

  private _updateActionStatus(actionId: string, status: ActionStatus, result?: string): void {
    this._actions = this._actions.map(a =>
      a.actionId === actionId ? { ...a, status, result } : a,
    );
  }

  private _handleTranscript(text: string, role: 'user' | 'agent', isInterim: boolean): void {
    if (isInterim) {
      // Update existing interim message or create new one
      const existingIdx = this._transcriptMessages.findIndex(
        m => m.role === role && m.isInterim,
      );
      if (existingIdx >= 0) {
        const updated = [...this._transcriptMessages];
        updated[existingIdx] = { ...updated[existingIdx], text, timestamp: new Date().toISOString() };
        this._transcriptMessages = updated;
      } else {
        this._addTranscriptMessage(text, role, true);
      }
    } else {
      // Final transcript: replace interim with final
      const interimIdx = this._transcriptMessages.findIndex(
        m => m.role === role && m.isInterim,
      );
      if (interimIdx >= 0) {
        const updated = [...this._transcriptMessages];
        updated[interimIdx] = { ...updated[interimIdx], text, isInterim: false, timestamp: new Date().toISOString() };
        this._transcriptMessages = updated;
      } else {
        this._addTranscriptMessage(text, role, false);
      }
    }
  }

  private _addTranscriptMessage(text: string, role: 'user' | 'agent', isInterim = false): void {
    this._transcriptCounter++;
    const msg: TranscriptMessage = {
      id: `msg-${this._transcriptCounter}`,
      role,
      text,
      timestamp: new Date().toISOString(),
      isInterim,
    };
    this._transcriptMessages = [...this._transcriptMessages, msg];
  }

  private _navigateBack(): void {
    navigateTo(this._previousPath);
  }

  private _renderAvatar() {
    if (this._agentInfo.avatar) {
      return html`<img class="cp-voice-call__avatar-img" src="${this._agentInfo.avatar}" alt="${this._agentInfo.name}" />`;
    }
    const initial = this._agentInfo.name.charAt(0).toUpperCase();
    return html`<span class="cp-voice-call__avatar-initial">${initial}</span>`;
  }

  private _renderConnecting() {
    return html`
      <div class="cp-voice-call__status">
        <div class="cp-voice-call__pulse-ring"></div>
        <div class="cp-voice-call__avatar">
          ${this._renderAvatar()}
        </div>
        <h2 class="cp-voice-call__agent-name">${this._agentInfo.name}</h2>
        <p class="cp-voice-call__agent-role">${this._agentInfo.role}</p>
        <p class="cp-voice-call__connecting-text">Verbindung wird hergestellt...</p>
      </div>
    `;
  }

  private _renderActive() {
    return html`
      <div class="cp-voice-call__status">
        <div class="cp-voice-call__avatar cp-voice-call__avatar--active">
          ${this._renderAvatar()}
        </div>
        <h2 class="cp-voice-call__agent-name">${this._agentInfo.name}</h2>
        <p class="cp-voice-call__agent-role">${this._agentInfo.role}</p>
        <p class="cp-voice-call__duration">${this._formatDuration(this._callDuration)}</p>
        ${!this._micAvailable && this._textMode ? html`
          <p class="cp-voice-call__text-mode-hint">Kein Mikrofon verfuegbar - Text-Modus aktiv</p>
        ` : nothing}
        <cp-audio-visualizer
          source="agent"
          ?active=${this._isAgentSpeaking}
        ></cp-audio-visualizer>
      </div>
      <div class="cp-voice-call__panels">
        <cp-call-transcript .messages=${this._transcriptMessages}></cp-call-transcript>
        <cp-action-log .actions=${this._actions}></cp-action-log>
      </div>
    `;
  }

  private _renderError() {
    return html`
      <div class="cp-voice-call__status">
        <div class="cp-voice-call__avatar cp-voice-call__avatar--error">
          ${this._renderAvatar()}
        </div>
        <h2 class="cp-voice-call__agent-name">${this._agentInfo.name}</h2>
        <p class="cp-voice-call__error-text">${this._errorMessage || 'Voice nicht konfiguriert'}</p>
        <a class="cp-voice-call__settings-link" href="#/settings" @click=${(e: Event) => { e.preventDefault(); navigateTo('/settings'); }}>
          Voice-Settings oeffnen
        </a>
      </div>
    `;
  }

  private _renderControls() {
    if (this._callState === 'error' || this._callState === 'ended') {
      return html`
        <div class="cp-voice-call__controls">
          <button class="cp-voice-call__btn cp-voice-call__btn--back" @click=${this._navigateBack}>
            Zurueck
          </button>
        </div>
      `;
    }

    if (this._callState === 'active') {
      return html`
        <cp-call-controls
          ?muted=${this._isMuted}
          input-mode="${this._inputMode}"
          ?disabled=${this._callState !== 'active'}
          ?text-mode=${this._textMode}
          @mute-toggle=${this._onMuteToggle}
          @hang-up=${this._onHangUp}
          @ptt-start=${this._onPttStart}
          @ptt-end=${this._onPttEnd}
          @mode-change=${this._onModeChange}
          @text-mode-toggle=${this._onTextModeToggle}
          @text-send=${this._onTextSend}
        ></cp-call-controls>
      `;
    }

    // Connecting state - show only hangup
    return html`
      <div class="cp-voice-call__controls">
        <button class="cp-voice-call__btn cp-voice-call__btn--hangup" @click=${this._onHangUp} title="Auflegen">
          <span class="cp-voice-call__icon">&#128222;</span>
        </button>
      </div>
    `;
  }

  render() {
    return html`
      <div class="cp-page-voice-call">
        <div class="cp-voice-call__content">
          ${this._callState === 'connecting' ? this._renderConnecting() : nothing}
          ${this._callState === 'active' ? this._renderActive() : nothing}
          ${this._callState === 'error' ? this._renderError() : nothing}
          ${this._callState === 'ended' ? html`
            <div class="cp-voice-call__status">
              <div class="cp-voice-call__avatar">
                ${this._renderAvatar()}
              </div>
              <h2 class="cp-voice-call__agent-name">${this._agentInfo.name}</h2>
              <p class="cp-voice-call__connecting-text">Anruf beendet</p>
            </div>
          ` : nothing}
        </div>
        ${this._renderControls()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-voice-call': CpPageVoiceCall;
  }
}
